/**
 * Executive Catalyst — Trading UI client. Read-only.
 *
 * Reads /api/v1/executive-catalysts/* over same-origin nginx proxy.
 * Never fabricates mentions, dates, or symbol mappings. When a symbol
 * mapping is inferred (vs. quoted), the UI surfaces an "INFERRED" badge.
 *
 * Marker strings (must appear in /etc/adversyn/trading-ui-markers.json):
 *   - "executive-catalysts"
 *   - "phase-catalyst-ui-v1"
 */
import { apiV1Base } from "./apiConfig";

type Envelope<T> = {
  ok: boolean;
  data: T | null;
  error?: { message: string; code: string } | null;
};

export interface ExecutiveEventRow {
  event_id?: number | string | null;
  event_date?: string | null;
  speaker_name?: string | null;
  speaker_role?: string | null;
  speaker_company?: string | null;
  event_type?: string | null;            // interview|keynote|earnings_call|other
  source?: string | null;
  source_url?: string | null;
  headline?: string | null;
  authority_score?: number | null;
}

export interface ExecutiveMentionRow {
  mention_id?: number | string | null;
  event_id?: number | string | null;
  symbol?: string | null;
  mentioned_company?: string | null;
  listing_class?: string | null;          // US|ADR|FOREIGN|UNKNOWN
  mention_type?: string | null;           // endorsement|criticism|competitive|partnership|other
  impact_direction?: string | null;       // bullish|bearish|volatility|uncertain
  expected_impact?: string | null;
  confidence?: number | null;
  risk_note?: string | null;
  verified_or_inferred?: string | null;   // verified|inferred|uncertain
  mapping_source?: string | null;
  eod_result?: string | null;
  // joined event columns
  event_date?: string | null;
  speaker_name?: string | null;
  speaker_role?: string | null;
  speaker_company?: string | null;
  source?: string | null;
  headline?: string | null;
  event_type?: string | null;
  authority_score?: number | null;
}

export interface ExecutivePayload<R> {
  rows: R[];
  row_count: number;
  filters: Record<string, unknown>;
  source_table: string;
}

async function exGet<T>(path: string): Promise<Envelope<T>> {
  try {
    const res = await fetch(`${apiV1Base()}${path}`, {
      headers: { accept: "application/json" },
      credentials: "include",
      cache: "no-store",
    });
    if (!res.ok) {
      return {
        ok: false,
        data: null,
        error: { message: `HTTP ${res.status}`, code: String(res.status) },
      };
    }
    return (await res.json()) as Envelope<T>;
  } catch (err: any) {
    return {
      ok: false,
      data: null,
      error: { message: err?.message || "network", code: "NETWORK" },
    };
  }
}

function qs(params: Record<string, string | number | undefined | null>): string {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  }
  return parts.length ? `?${parts.join("&")}` : "";
}

export const getExecutiveEvents = (
  start?: string,
  end?: string,
  speaker?: string,
  event_type?: string,
  limit = 200,
) =>
  exGet<ExecutivePayload<ExecutiveEventRow>>(
    `/executive-catalysts/events${qs({ start, end, speaker, event_type, limit })}`,
  );

export const getExecutiveMentions = (
  start?: string,
  end?: string,
  symbol?: string,
  mention_type?: string,
  impact_direction?: string,
  verified_or_inferred?: string,
  limit = 200,
) =>
  exGet<ExecutivePayload<ExecutiveMentionRow>>(
    `/executive-catalysts/mentions${qs({
      start,
      end,
      symbol,
      mention_type,
      impact_direction,
      verified_or_inferred,
      limit,
    })}`,
  );

export const getExecutiveBySymbol = (symbol: string, start?: string, end?: string, limit = 200) =>
  exGet<ExecutivePayload<ExecutiveMentionRow>>(
    `/executive-catalysts/by-symbol/${encodeURIComponent(symbol)}${qs({ start, end, limit })}`,
  );
